// Sales → Team (owners only): who can use the Sales portal and in which role.
// Add by email (they must have signed in once), change a role, remove. The
// server keeps the last owner and explains why; that message is shown.

import { useState } from "react";
import { fill } from "../../i18n/dictionary";
import { SALES_ROLES, type SalesRole, type TeamMember } from "../../data/sales";
import { Button, Card, ConfirmDialog, ErrorPanel, Field, INPUT, LoadingLine, TableCard, TD, TH, formatDate, looksLikeEmail, useLoad } from "../admin/ui";
import { Forbidden, SectionTitle, StatusLine, salesErrorCopy, useSales } from "./shared";

export function Team() {
  const { ds, t, can, locale, me } = useSales();
  if (!can("team")) return <Forbidden text={t.shell.ownersOnly} />;
  return <TeamInner key={me.email} ds={ds} t={t} locale={locale} myEmail={me.email} />;
}

function TeamInner({ ds, t, locale, myEmail }: { ds: ReturnType<typeof useSales>["ds"]; t: ReturnType<typeof useSales>["t"]; locale: ReturnType<typeof useSales>["locale"]; myEmail: string }) {
  const load = useLoad(() => ds.team(), [ds]);
  const [email, setEmail] = useState("");
  const [role, setRole] = useState<SalesRole>("sales");
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<{ tone: "good" | "attention"; text: string } | null>(null);
  const [removing, setRemoving] = useState<TeamMember | null>(null);
  const [removeError, setRemoveError] = useState("");

  async function add() {
    if (!looksLikeEmail(email)) return setMsg({ tone: "attention", text: t.team.errEmail });
    setBusy(true);
    setMsg(null);
    try {
      await ds.addTeamMember(email.trim(), role);
      setEmail("");
      setMsg({ tone: "good", text: t.team.added });
      await load.refresh();
    } catch (err) {
      setMsg({ tone: "attention", text: salesErrorCopy(t, err) });
    } finally {
      setBusy(false);
    }
  }

  async function changeRole(m: TeamMember, r: SalesRole | null) {
    setBusy(true);
    setMsg(null);
    try {
      await ds.setRole(m.accountId, r);
      if (r !== null) setMsg({ tone: "good", text: t.common.saved });
      await load.refresh();
      return true;
    } catch (err) {
      const text = salesErrorCopy(t, err);
      if (r === null) setRemoveError(text);
      else setMsg({ tone: "attention", text });
      // Put the select back on what the server still holds.
      await load.refresh();
      return false;
    } finally {
      setBusy(false);
    }
  }

  return (
    <section aria-labelledby="team-title" style={{ display: "flex", flexDirection: "column", gap: 16 }}>
      <SectionTitle id="team-title">{t.team.title}</SectionTitle>
      <p style={{ margin: "-6px 0 0", color: "var(--ink-soft)", fontSize: 15 }}>{t.team.intro}</p>
      <ul style={{ margin: 0, paddingInlineStart: 18, color: "var(--ink-soft)", fontSize: 14 }}>
        {SALES_ROLES.map((r) => (
          <li key={r}>
            <strong>{t.roles[r]}:</strong> {t.roles[`${r}Hint`]}
          </li>
        ))}
      </ul>

      <Card title={t.team.addTitle}>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(min(100%, 200px), 1fr))", gap: 10, alignItems: "end" }}>
          <Field label={t.team.email} type="email" autoComplete="off" value={email} onChange={(e) => setEmail(e.target.value)} hint={t.team.addHint} />
          <div style={{ display: "flex", flexDirection: "column", gap: 5, alignSelf: "start" }}>
            <label htmlFor="team-new-role" style={{ fontSize: 13.5, fontWeight: 600, color: "var(--ink-soft)" }}>
              {t.team.role}
            </label>
            <select id="team-new-role" value={role} onChange={(e) => setRole(e.target.value as SalesRole)} style={INPUT}>
              {SALES_ROLES.map((r) => (
                <option key={r} value={r}>
                  {t.roles[r]}
                </option>
              ))}
            </select>
          </div>
        </div>
        <div style={{ marginTop: 12, display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
          <Button onClick={() => void add()} disabled={busy}>
            {t.team.add}
          </Button>
          <StatusLine msg={msg} />
        </div>
      </Card>

      {load.loading && !load.data ? (
        <LoadingLine label={t.common.loading} />
      ) : load.error || !load.data ? (
        <ErrorPanel title={t.common.errorTitle} message={salesErrorCopy(t, load.error)} onRetry={load.reload} retryLabel={t.common.retry} />
      ) : (
        <TableCard label={t.team.title} minWidth={560}>
          <thead>
            <tr>
              <th scope="col" style={TH}>{t.team.colEmail}</th>
              <th scope="col" style={TH}>{t.team.colRole}</th>
              <th scope="col" style={TH}>{t.team.colAdded}</th>
              <th scope="col" style={TH}>{t.invoices.colActions}</th>
            </tr>
          </thead>
          <tbody>
            {load.data.map((m) => (
              <tr key={m.accountId}>
                <th scope="row" style={{ ...TD, textAlign: "start", fontWeight: 600, overflowWrap: "anywhere" }}>
                  {m.email}
                  {m.email === myEmail && <span style={{ marginInlineStart: 6, fontWeight: 400, color: "var(--ink-faint)" }}>({t.team.you})</span>}
                </th>
                <td style={TD}>
                  <select
                    aria-label={fill(t.team.roleFor, { email: m.email })}
                    value={m.role}
                    disabled={busy}
                    onChange={(e) => void changeRole(m, e.target.value as SalesRole)}
                    style={{ ...INPUT, width: "auto" }}
                  >
                    {SALES_ROLES.map((r) => (
                      <option key={r} value={r}>
                        {t.roles[r]}
                      </option>
                    ))}
                  </select>
                </td>
                <td style={{ ...TD, whiteSpace: "nowrap" }}>{formatDate(locale, m.addedAt)}</td>
                <td style={TD}>
                  <Button variant="secondary" disabled={busy} aria-label={`${t.team.remove}: ${m.email}`} onClick={() => setRemoving(m)}>
                    {t.team.remove}
                  </Button>
                </td>
              </tr>
            ))}
          </tbody>
        </TableCard>
      )}

      {removing && (
        <ConfirmDialog
          title={fill(t.team.removeTitle, { email: removing.email })}
          body={t.team.removeBody}
          confirmLabel={t.team.remove}
          cancelLabel={t.common.cancel}
          busy={busy}
          error={removeError}
          onConfirm={() =>
            void changeRole(removing, null).then((ok) => {
              if (ok) setRemoving(null);
            })
          }
          onCancel={() => {
            setRemoving(null);
            setRemoveError("");
          }}
        />
      )}
    </section>
  );
}
